import type {
  InvoiceItemFulfillment,
  KitchenBoardResponse,
  KitchenWorkflow,
} from "@/lib/api/types";
import type { PlateLane } from "./lateness";

/**
 * THE KITCHEN WORKFLOW ON THE DEVICE (T1, D164) — the frontend twin of
 * `apps/backend/src/modules/restaurant/policies/kitchen-workflow.policy.ts`.
 *
 * The server derives the workflow from the two display/print booleans plus
 * `kitchen_one_tap` and sends it on the board payload. The same names and the
 * same answers live here, plus the two only the board needs: which lanes it
 * draws (`boardLanes`) and which plates a hand-over serves (`handOverStates`).
 * Pure: no React, no storage.
 */

export const KITCHEN_WORKFLOWS: readonly KitchenWorkflow[] = [
  "PAPER_ONLY",
  "PAPER_AND_PASS",
  "ONE_TAP_SCREEN",
  "TWO_TAP_SCREEN",
  "NO_SCREEN_NO_PRINT",
];

export function workflowOf(display: boolean, print: boolean, oneTap: boolean): KitchenWorkflow {
  if (display && print) return "PAPER_AND_PASS";
  if (display) return oneTap ? "ONE_TAP_SCREEN" : "TWO_TAP_SCREEN";
  if (print) return "PAPER_ONLY";
  return "NO_SCREEN_NO_PRINT";
}

/**
 * The workflow as the SETTINGS read states it (the till and the settings page
 * read settings, not the board). An API from before D164 sends no
 * `kitchenWorkflow`; the two booleans then decide, one-tap reading as off.
 */
export function settingsWorkflow(
  settings:
    | {
        kitchenWorkflow?: KitchenWorkflow;
        kitchenDisplayEnabled: boolean;
        kitchenPrintEnabled: boolean;
        kitchenOneTap?: boolean;
      }
    | null
    | undefined,
): KitchenWorkflow {
  if (!settings) return "NO_SCREEN_NO_PRINT";
  return (
    settings.kitchenWorkflow ??
    workflowOf(settings.kitchenDisplayEnabled, settings.kitchenPrintEnabled, settings.kitchenOneTap ?? false)
  );
}

/**
 * The board's workflow. An API from before the workflow round sends none: a
 * display-on store then behaves exactly as it did (today's two-tap screen).
 */
export function boardWorkflow(
  board: Pick<KitchenBoardResponse, "workflow" | "kitchenDisplayEnabled"> | null | undefined,
): KitchenWorkflow {
  if (board?.workflow) return board.workflow;
  return board?.kitchenDisplayEnabled ? "TWO_TAP_SCREEN" : "NO_SCREEN_NO_PRINT";
}

export function autoStartsAtFire(workflow: KitchenWorkflow): boolean {
  return workflow === "NO_SCREEN_NO_PRINT" || workflow === "ONE_TAP_SCREEN";
}

export function printStartsFood(workflow: KitchenWorkflow): boolean {
  return workflow === "PAPER_ONLY" || workflow === "PAPER_AND_PASS";
}

export function recordsReady(workflow: KitchenWorkflow): boolean {
  return workflow === "ONE_TAP_SCREEN" || workflow === "TWO_TAP_SCREEN";
}

/** Whether this workflow draws a board at all (the two display-off ones do not). */
export function hasBoard(workflow: KitchenWorkflow): boolean {
  return workflow === "PAPER_AND_PASS" || workflow === "ONE_TAP_SCREEN" || workflow === "TWO_TAP_SCREEN";
}

/**
 * Whether a cook may START a plate from the board. In a paper workflow the
 * printed ticket starts the food (D18 actuator); a Start tapped on the pass
 * screen would bypass the one actuator nobody can forget, so a QUEUED plate
 * there only carries «لم تُطبع بعد».
 */
export function startsFromBoard(workflow: KitchenWorkflow): boolean {
  return !printStartsFood(workflow);
}

/** One lane of the board: its id (the CSS's `data-lane`) and the states it holds. */
export type BoardLane = {
  id: PlateLane;
  states: readonly InvoiceItemFulfillment[];
};

const NEW_LANE: BoardLane = { id: "QUEUED", states: ["QUEUED"] };
const COOKING_LANE: BoardLane = { id: "PREPARING", states: ["PREPARING"] };
/** One-tap and paper+pass: one «في التحضير» lane for everything not ready. */
const COOKING_MERGED_LANE: BoardLane = { id: "PREPARING", states: ["QUEUED", "PREPARING"] };
const READY_LANE: BoardLane = { id: "READY", states: ["READY"] };

/**
 * THE LANES BY WORKFLOW (plan §4.10):
 *  - two-tap: «جديد / في التحضير / جاهز»;
 *  - one-tap: «في التحضير» (QUEUED + PREPARING) / «جاهز»;
 *  - paper + pass: «في التحضير» (a QUEUED card carries «لم تُطبع بعد») / «جاهز»;
 *  - the two display-off workflows draw no lanes (the display-off screen).
 */
export function boardLanes(workflow: KitchenWorkflow): readonly BoardLane[] {
  switch (workflow) {
    case "TWO_TAP_SCREEN":
      return [NEW_LANE, COOKING_LANE, READY_LANE];
    case "ONE_TAP_SCREEN":
    case "PAPER_AND_PASS":
      return [COOKING_MERGED_LANE, READY_LANE];
    case "PAPER_ONLY":
    case "NO_SCREEN_NO_PRINT":
      return [];
  }
}

/** The board lane a plate in `state` is drawn in, or null when none holds it. */
export function laneOfState(workflow: KitchenWorkflow, state: InvoiceItemFulfillment): PlateLane | null {
  return boardLanes(workflow).find((lane) => lane.states.includes(state))?.id ?? null;
}

/**
 * WHICH PLATES ONE HAND-OVER SERVES (plan §3.1). The expediter holds
 * `ITEM_SERVE` and no `ITEM_PREPARE`, so in a paper workflow nobody records
 * READY: the one tap hands over every plate the kitchen has STARTED
 * (PREPARING → SERVED is legal, D18). Where the screen records READY, only
 * what is ready goes.
 */
export function handOverStates(workflow: KitchenWorkflow): readonly ("PREPARING" | "READY")[] {
  return printStartsFood(workflow) ? ["PREPARING", "READY"] : ["READY"];
}
